/* Do not use events or es6-events packages, because it has own error handler */
import React, { Component } from "react"
import Events from "tiny-emitter"
import { connect } from "react-redux"
import {
  compose,
  withProps,
  withPropsOnChange,
  withState,
  lifecycle,
} from "recompact"
import uuid4 from "uuid/v4"
import toString from "lodash/toString"
import omit from "lodash/omit"
import workspace from "src/hocs/workspace"
import identity from "lodash/identity"
import get from "lodash/get"
import { prepareHocPluginsApplier } from "src/engine/helpers"
import entityActionTypes from "../actions/types"
import { selectors, actions } from ".."

function createTaskResponser(emitter) {
  let required = false
  let outsideReject
  let rejectedValue
  const response = new Promise((resolve, reject) => {
    outsideReject = reject
    let onError
    const onSuccess = () => {
      emitter.off(`error`, onError)
      resolve()
    }

    onError = error => {
      emitter.off(`success`, onSuccess)
      if (required) {
        setTimeout(() => {
          // eslint-disable-lineq
          reject(error)
        })
      } else {
        rejectedValue = error
      }
    }

    emitter.once(`success`, onSuccess)
    emitter.once(`error`, onError)
  })

  /* I use Promise, which will never be rejected, because hoc API can be used
   * without subscribing to rejections (errors also handled by the notification
   * module), so to prevent unhandledRejection exception I should not return
   * rejected promise, until it will be required */
  function performRejectable() {
    /* */
    if (!required) {
      required = true
      if (rejectedValue && outsideReject) {
        setTimeout(() => {
          // eslint-disable-lineq
          outsideReject(rejectedValue)
        })
      }
    }

    /* Remove temporary promise API */
    delete response.then
    delete response.catch
    delete response.finally
  }

  response.then = function temporaryThen(r, j) {
    if (j) {
      performRejectable()
    }
    return Reflect.apply(Promise.prototype.then, response, [r, j])
  }

  response.catch = function temporaryCatch(handler) {
    if (handler) {
      performRejectable()
    }
    return Reflect.apply(Promise.prototype.catch, response, handler)
  }

  response.finally = function tomporaryFinally(...args) {
    performRejectable()
    return Reflect.apply(Promise.prototype.finally, response, args)
  }

  return response
}

/* This HOC supports model, built on specification RD-1
Specification: http://confluence.infotech.team/pages/viewpage.action?pageId=14947085

In short, your domain must contains .reducer and .saga/.middleware properties.

API Reference:
--

**Available config:**
  - `to` - name of final prop, which will get entity state
  - `propName` - same as `to`
  - `model` - entity model
  - `id` - entity ID
  - `plugins` - array of plugins

Also config can be a function, which accept current props.
*/

export default function(config = {}) {
  const { plugins } = config
  const applyPlugins = prepareHocPluginsApplier(plugins)

  const enhancer = compose(
    withProps(props =>
      omit(typeof config === `function` ? config(props) : config, [`plugins`]),
    ),
    applyPlugins(`config`),
    /* resetInitial is a random number, which force reinitial entity app.
     * Each time when you need to reset whole entity, you should pass
     * to resetToInitial unique number */
    withState(`resetInitial`, `resetToInitial`, 0),
    applyPlugins(`state`),
    /* (Re)initial stage  */
    withPropsOnChange(
      [`id`, `model`, `to`, `propName`, `resetInitial`, `onError`],
      props => {
        const {
          /* Target entity id (keep empty for create new entity) */
          id,
          /* Domain based on crud  */
          model,
          /* Target prop */
          to,
          /* More human friendly `to` synonym */
          propName,
        } = props

        const isVirtual = !id

        if (!model) {
          throw new Error(`entity enhancer expects model`)
        }

        return {
          isVirtual,
          id: isVirtual ? uuid4() : toString(id),
          to: propName || to || `entity`,
          emitter: new Events(),
        }
      },
    ),
    applyPlugins(`initial`),
    /* Select businness data from entity */
    connect(
      (state, props) => {
        const {
          /* Specifies the props scope where all related to api data hosted */
          to,
          /* Target entity id (keep empty for create new entity) */
          id,
          /* Domain based on crud  */
          model,
          /* Id id has not passed, the entity is virtual */
          isVirtual,
          /* The way to observe entity async tasks results */
          emitter,
        } = props

        const createdId = selectors.createdId(state, model, id)
        const nextId = createdId || id
        const data = selectors.data(state, model, nextId)
        const error = selectors.error(state, model, id)
        const pending = selectors.pending(state, model, id)
        const process = selectors.process(state, model, id)

        const nextProps = {
          [to]: {
            isVirtual,
            isCreated: Boolean(createdId),
            initialId: id,
            createdId,
            error,
            model,
            id: nextId,
            pending,
            process,
            data,
            emitter,
          },
        }

        return nextProps
      },
      dispatch => ({
        dispatch,
      }),
      (stateProps, dispatchProps, ownProps) => ({
        ...stateProps,
        ...dispatchProps,
        ...ownProps,
      }),
    ),
    BaseComponent =>
      class Api extends Component {
        // eslint-disable-next-line camelcase
        UNSAFE_componentWillMount() {
          const { props } = this
          this.api = {
            read: () => {
              const { props } = this
              const { to, dispatch } = props
              const { emitter, model, id } = props[to]

              dispatch(actions.creators.read(model, id))

              return createTaskResponser(emitter)
            },
            create: (data, callback) => {
              const { props } = this
              const { to, saveTransform = identity, dispatch } = props
              const { emitter, model, id } = props[to]

              dispatch(
                actions.creators.create(
                  model,
                  id,
                  saveTransform(data, this.props),
                  callback,
                ),
              )

              return createTaskResponser(emitter)
            },
            update: data => {
              const { props } = this
              const { to, saveTransform = identity, dispatch } = props
              const { emitter, model, id } = props[to]

              dispatch(
                actions.creators.update(
                  model,
                  id,
                  saveTransform(data, this.props),
                ),
              )

              return createTaskResponser(emitter)
            },
            remove: () => {
              const { props } = this
              const { to, dispatch } = props
              const { emitter, model, id } = props[to]

              dispatch(actions.creators.remove(model, id))

              return createTaskResponser(emitter)
            },
            // Magic function, which save existen elements and create new elements
            save: data => {
              const { props } = this
              const { to, saveTransform = identity, dispatch } = props
              const { emitter, model, id, isVirtual } = props[to]

              dispatch(
                actions.creators[isVirtual ? `create` : `update`](
                  model,
                  id,
                  saveTransform(data, this.props),
                ),
              )

              return createTaskResponser(emitter)
            },
            release: () => {
              // @TODO: Temporary disabled items release to avoid bugs, needs maximum attention!
              /* const { to } = this.props
            this.props.dispatch(
              actions.creators.release(
                this.props[to].model,
                customId || this.props[to].id,
              )
            ) */
            },
            involve: customId => {
              const { props } = this
              const { to, dispatch } = props
              const { model, id } = props[to]

              dispatch(actions.creators.involve(model, customId || id))
            },
          }

          this.cachedTo = {
            ...props[props.to],
            api: this.api,
          }
        }

        // eslint-disable-next-line camelcase
        UNSAFE_componentWillReceiveProps(nextProps) {
          /* Enhance current entity response with API.
           * I do it mutable. Why?
           * First reason - response readonly and it will give no corruptions
           * Second reason - prevent of creating new object by spread
           */
          const { props } = this
          if (nextProps[nextProps.to] !== props[props.to]) {
            this.cachedTo = {
              ...nextProps[nextProps.to],
              api: this.api,
            }
          }
        }

        render() {
          const { to } = this.props
          const nextProps = {
            ...this.props,
            [to]: this.cachedTo,
          }
          return <BaseComponent {...nextProps} />
        }
      },
    applyPlugins(`connect`),
    withProps(props => ({
      reinitialize: newProps => {
        const { to, restrictFetch } = newProps
        /* If entity item is not virtual, good idea to fetch it
         * right now automatically */
        if (newProps[to].id && !newProps[to].isVirtual && !restrictFetch) {
          /* Read entity */
          props[to].api.read()
          /* Involve entity (say which it is required for this component ) */
          props[to].api.involve()
        }
      },
    })),
    workspace,
    applyPlugins(`workspace`),
    lifecycle({
      componentDidMount() {
        this.pending = false
        this.props.reinitialize(this.props)
      },
      componentWillUnmount() {
        const { to } = this.props
        const item = this.props[to]

        /* We should release current entity, if exists */
        item.api.release(this.props[to].id)

        /* Cancel residual pending */
        if (this.pending) {
          this.props.workspace.removeLabel(`pending`)
        }

        if (item.id !== item.initialId) {
          /* Also release virtual entity if it were */
          item.api.release(this.props[to].initialId)
        }
      },
      // eslint-disable-next-line camelcase
      UNSAFE_componentWillReceiveProps(nextProps) {
        /* If emitter is not equals to last emitter, then entity hoc reinitialized */
        if (nextProps.emitter !== this.props.emitter) {
          this.props.reinitialize(nextProps)
        }

        // Check for id or model has been changed
        /* eslint eqeqeq: 0 */
        if (
          /* Non-strict eq, because we expect a string, contains number, that should be equal */
          nextProps[nextProps.to].id != this.props[this.props.to].id ||
          nextProps[nextProps.to].model !== this.props[this.props.to].model
        ) {
          // We should release previous id
          this.props[this.props.to].api.release()
        }

        // Check for staying in the form, after creating entity
        if (
          nextProps[nextProps.to].createdId &&
          nextProps[nextProps.to].initialId !==
            nextProps[nextProps.to].createdId
        ) {
          if (
            this.props[this.props.to].createdId ===
            nextProps[nextProps.to].createdId
          ) {
            /* Initial attention about redirection to created item */
            throw new Error(
              `You should redirect your page to created item, using createdId property`,
            )
          } else {
            // Reset form
            nextProps.resetToInitial(Math.random())

            if (typeof nextProps.onCreate === `function`) {
              /* Use onCreate handler once */
              const response = nextProps.onCreate(nextProps)
              if (typeof response === `function`) {
                response(nextProps[nextProps.to])
              }
            }
          }
          // Check whether an item was deleted
        } else if (
          !nextProps[nextProps.to].data &&
          this.props[this.props.to].data
        ) {
          // Seems like item has deleted
          if (typeof nextProps.onDelete === `function`) {
            /* Use onCreate handler once */
            const response = nextProps.onDelete(nextProps)
            if (typeof response === `function`) {
              response(nextProps[nextProps.to])
            }
          }
        } else if (
          typeof nextProps.onUpdate === `function` &&
          get(this, `props.${this.props.to}.process.type`) ===
            entityActionTypes.update &&
          this.props[this.props.to].error === null &&
          this.props[this.props.to].pending &&
          !nextProps[nextProps.to].pending
        ) {
          const response = nextProps.onUpdate(nextProps)
          if (typeof response === `function`) {
            response(nextProps[nextProps.to])
          }
        }

        // Check for an error
        if (nextProps[nextProps.to].error !== this.props[this.props.to].error) {
          if (nextProps[nextProps.to].error) {
            const { error } = nextProps[nextProps.to]
            nextProps[nextProps.to].emitter.emit(`error`, error)
            this.props.workspace.addLabel(`error`)

            // Run onError events
            if (typeof nextProps.onError === `function`) {
              /* Use onCreate handler once */
              const response = nextProps.onError(error)
              if (typeof response === `function`) {
                response(nextProps[nextProps.to])
              }
            }
          } else {
            this.props.workspace.removeLabel(`error`)
          }
        }

        // Check for pending and pass it to workspace
        if (
          nextProps[nextProps.to].pending !== this.props[this.props.to].pending
        ) {
          if (nextProps[nextProps.to].pending) {
            this.pending = true
            this.props.workspace.addLabel(`pending`)
          } else {
            this.pending = false
            this.props.workspace.removeLabel(`pending`)
            // Check after-pending status
            nextProps[nextProps.to].emitter.emit(`success`)
          }
        }
      },
    }),
    applyPlugins(`after`),
  )
  return function CrudEnhancer(OriginComponent) {
    return enhancer(OriginComponent)
  }
}

/*
  На вход entity поступает config (props example  - { id, to, model })
  На выход [to] : data (example -
    plan: {
      api: {read: ƒ, create: ƒ, update: ƒ, remove: ƒ, save: ƒ, …}
      createdId: undefined
      data: {startDateTime: 1561323600000, endDateTime: 1580418000000, locked: false, name: "1anew", tags: Array(0), …}
      emitter: E {e: {…}}
      error: null
      id: "714"
      initialId: "714"
      isCreated: false
      isVirtual: false
      model: Model {name: "plan", getRestUrn: ƒ, primaryKey: "id", denormalize: ƒ, schema: Schema}
      pending: false
    }
)
  emitter и createTaskResponser используются для CRUD. Вова называет функции crud: `Magic function`...
  lifecycle для involve/include/release
*/
